import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import api from '../api/client'
import { toFarsiDigits, gregorianToJalali } from '../assets/utils'

const money = (v) => toFarsiDigits(Number(v || 0).toLocaleString('en-US'))

const today = () => {
    const d = new Date()
    return gregorianToJalali(d.toISOString().slice(0, 10))
}

const th = {
    border: '1px solid #b9c0da', padding: '9px 8px', background: '#1a237e',
    color: '#fff', fontWeight: 800, fontSize: 13,
}
const td = { border: '1px solid #cfd6e8', padding: '8px', textAlign: 'center', fontSize: 13.5 }
const totTd = { ...td, background: '#fff3e0', fontWeight: 800 }

/** نسخهٔ چاپیِ گزارش پیش‌پرداخت — A4 افقی، بدونِ منو و دکمه‌های سامانه. */
const PrepaymentPrint = () => {
    const { id } = useParams()
    const [sp] = useSearchParams()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [error, setError] = useState('')

    const ptype = sp.get('prepayment_type') || ''
    const withDiff = (sp.get('with_diff') ?? '1') !== '0'
    const scope = sp.get('scope') || 'contract'
    const from = sp.get('from') || ''
    const to = sp.get('to') || ''
    const q = sp.get('q') || ''
    const sort = sp.get('sort') || 'date'
    const dir = sp.get('dir') || 'asc'

    useEffect(() => {
        let alive = true
        api.get(`contracts/${id}/prepayment-report/`, {
            params: {
                prepayment_type: ptype, with_diff: withDiff ? 1 : 0,
                scope, from, to, q, sort, dir,
            },
        }).then((r) => { if (alive) setData(r.data) })
            .catch(() => { if (alive) setError('گزارش یافت نشد.') })
        return () => { alive = false }
    }, [id, ptype, withDiff, scope, from, to, q, sort, dir])

    useEffect(() => {
        if (data) { const t = setTimeout(() => window.print(), 600); return () => clearTimeout(t) }
        return undefined
    }, [data])

    const rows = (data && data.rows) || []
    const s = (data && data.summary) || {}
    const c = (data && data.contract) || {}

    return (
        <Box dir="rtl" style={{ background: '#eef1f8', minHeight: '100vh', padding: '18px 10px' }}>
            <style>{`
                @page { size: A4 landscape; margin: 10mm; }
                @media print {
                    body { background: #fff !important; }
                    .no-print { display: none !important; }
                    .sheet { box-shadow: none !important; margin: 0 !important; width: 100% !important; }
                }
            `}</style>

            <Box className="no-print" display="flex" gap={1} justifyContent="center" style={{ marginBottom: 14 }}>
                <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
                    style={{ background: '#1a237e', color: '#fff', fontWeight: 800 }}>
                    چاپ
                </Button>
                <Button variant="outlined" startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/contracts/${id}/prepayment`)}
                    style={{ fontWeight: 800 }}>
                    بازگشت
                </Button>
            </Box>

            <Box className="sheet" style={{
                background: '#fff', maxWidth: 1120, margin: '0 auto', padding: '22px 26px',
                borderRadius: 10, boxShadow: '0 10px 30px rgba(26,35,126,.12)',
            }}>
                {error ? (
                    <Typography style={{ textAlign: 'center', padding: 40, color: '#c62828' }}>{error}</Typography>
                ) : !data ? (
                    <Box textAlign="center" p={6}><CircularProgress size={30} /></Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start"
                            style={{ borderBottom: '2px solid #1a237e', paddingBottom: 10, marginBottom: 14 }}>
                            <Box>
                                <Typography style={{ fontWeight: 900, fontSize: 18, color: '#1a237e' }}>
                                    گزارش ریز حواله‌های پیش‌پرداخت
                                </Typography>
                                <Typography style={{ fontSize: 12.5, color: '#555', marginTop: 4 }}>
                                    سازمان: {c.organization || '—'} &nbsp;|&nbsp; پیمانکار: {c.contractor || '—'}
                                </Typography>
                            </Box>
                            <Box style={{ textAlign: 'left', fontSize: 12.5, color: '#444', lineHeight: 2 }}>
                                <div>قرارداد: <b>{toFarsiDigits(c.number || '')}</b></div>
                                <div>تاریخ چاپ: {today()}</div>
                            </Box>
                        </Box>

                        <Box style={{
                            background: '#f4f6ff', border: '1px solid #d6ddf5', borderRadius: 8,
                            padding: '8px 12px', marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#1a237e',
                        }}>
                            نوع پیش‌پرداخت: <span style={{ color: '#6a1b9a' }}>{ptype || 'همه'}</span>
                            <span style={{ color: '#7b86a3', fontWeight: 600 }}>
                                {' '}— تفاوت نرخ روز: {withDiff ? 'نمایش داده می‌شود' : 'نمایش داده نمی‌شود'}
                            </span>
                            {data && data.scope && data.scope.contracts > 1 && (
                                <span style={{ color: '#1a237e' }}>
                                    {' '}— دامنه: {toFarsiDigits(data.scope.contracts)} قرارداد
                                </span>
                            )}
                            {from && <span style={{ color: '#7b86a3' }}> — از {toFarsiDigits(from)}</span>}
                            {to && <span style={{ color: '#7b86a3' }}> — تا {toFarsiDigits(to)}</span>}
                        </Box>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={th}>حواله</th>
                                    <th style={th}>تاریخ</th>
                                    <th style={th}>سال مالی</th>
                                    <th style={th}>نوع پیش‌پرداخت</th>
                                    <th style={th}>پیش‌پرداخت</th>
                                    {withDiff && <th style={th}>تفاوت نرخ روز</th>}
                                    <th style={th}>تسویه</th>
                                    <th style={th}>تسویهٔ سال‌های قبل</th>
                                    {withDiff && <th style={th}>جمع پیش‌پرداخت</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={r.id} style={{ background: i % 2 ? '#f7f9ff' : '#fff' }}>
                                        <td style={td}>{toFarsiDigits(r.number)}</td>
                                        <td style={td}>{gregorianToJalali(r.date)}</td>
                                        <td style={td}>{toFarsiDigits(r.fiscal_year || '')}</td>
                                        <td style={td}>{r.prepayment_type || '—'}</td>
                                        <td style={td}>{money(r.base)}</td>
                                        {withDiff && <td style={td}>{money(r.diff)}</td>}
                                        <td style={td}>{money(r.back)}</td>
                                        <td style={td}>{money(r.back_old)}</td>
                                        {withDiff && <td style={{ ...td, fontWeight: 800 }}>{money(r.amount)}</td>}
                                    </tr>
                                ))}
                                <tr>
                                    <td style={totTd} colSpan={4}>جمع کل ({toFarsiDigits(s.count || 0)} حواله)</td>
                                    <td style={totTd}>{money(s.base)}</td>
                                    {withDiff && <td style={totTd}>{money(s.diff)}</td>}
                                    <td style={totTd}>{money(s.back)}</td>
                                    <td style={totTd}>{money(s.back_old)}</td>
                                    {withDiff && <td style={totTd}>{money(s.amount)}</td>}
                                </tr>
                                <tr>
                                    <td style={{ ...totTd, background: '#e8f5e9' }} colSpan={withDiff ? 8 : 6}>
                                        مانده (جمع پیش‌پرداخت − تسویه)
                                    </td>
                                    <td style={{ ...totTd, background: '#e8f5e9' }}>{money(s.pending)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <Box display="flex" justifyContent="space-between" style={{ marginTop: 26, fontSize: 12.5, color: '#555' }}>
                            <div>مهر و امضای سازمان: ............................</div>
                            <div>مهر و امضای پیمانکار: ............................</div>
                        </Box>
                        <Typography style={{ marginTop: 14, fontSize: 11, color: '#9aa1b9', textAlign: 'center' }}>
                            ارجان پیمان — گزارش خودکار از داده‌های سامانه؛ بدونِ مهر و امضا فاقدِ اعتبارِ رسمی است.
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    )
}

export default PrepaymentPrint
